// NIP-01 kind:0 profile helpers. Pure functions — no relay I/O lives
// here. Used by the useProfile hook (relay fetch + cache) and by every
// component that renders identity.

// Parse a kind:0 event's content JSON into a normalized profile shape.
// Returns null for any unparseable input. On a successful parse, every
// field is present as a string ('' when missing) so callers can do
// `profile.picture && <img />` without an extra null check.
//
// Reads the canonical NIP-01 snake_case fields and the camelCase
// variants some clients emit.
export function parseProfileContent(jsonString) {
  if (!jsonString || typeof jsonString !== 'string') return null
  let raw
  try { raw = JSON.parse(jsonString) }
  catch { return null }
  if (!raw || typeof raw !== 'object') return null

  return {
    name: stringOrEmpty(raw.name),
    displayName: stringOrEmpty(raw.display_name || raw.displayName),
    picture: stringOrEmpty(raw.picture || raw.image),
    nip05: stringOrEmpty(raw.nip05),
    about: stringOrEmpty(raw.about),
  }
}

function stringOrEmpty(v) {
  return typeof v === 'string' ? v.trim() : ''
}

// Choose the best identity label: explicit display_name, then short
// name, then a truncated npub. All identity surfaces route through
// this so the header, account modal, and any future surface stay
// aligned.
export function displayNameOf(profile, npub) {
  return profile?.displayName
    || profile?.name
    || shortNpub(npub)
}

// Truncate an npub for chip-style display: first 8 chars + … + last 4.
// Returns '' for falsy / non-string input so callers can compose
// without null checks.
export function shortNpub(npub) {
  if (!npub || typeof npub !== 'string') return ''
  if (npub.length <= 12) return npub
  return `${npub.slice(0, 8)}…${npub.slice(-4)}`
}

// True if the string looks like a plausible NIP-05 identifier:
// exactly one @, non-empty localpart, domain with at least one dot
// and characters on both sides of it. We do NOT make HTTPS roundtrips
// here — verification against /.well-known/nostr.json is Phase 2+.
export function isLikelyValidNip05(s) {
  if (!s || typeof s !== 'string') return false
  const t = s.trim()
  const at = t.indexOf('@')
  if (at < 1 || at !== t.lastIndexOf('@')) return false
  const domain = t.slice(at + 1)
  const dot = domain.indexOf('.')
  return dot > 0 && dot < domain.length - 1
}

// NIP-05 display formatting: the localpart "_" is a special "root"
// marker that should render as the bare domain (Damus / Primal
// convention). Returns '' for malformed input so callers can guard
// with a single truthy check.
export function formatNip05(s) {
  if (!isLikelyValidNip05(s)) return ''
  const t = s.trim()
  if (t.startsWith('_@')) return t.slice(2)
  return t
}

// Build the JSON content string for a kind:0 event. Overlays the
// editable fields onto existingProfile so we don't strip unknown
// custom-client data (banner, website, lud16, etc.) on save. Skips
// empty-string values so we don't publish them.
//
// Maps our JS-side camelCase to canonical NIP-01 snake_case.
export function buildProfileContent(profile, existingProfile = {}) {
  const merged = { ...existingProfile }
  // If our own camelCase ever leaked into the raw existing content,
  // drop it — we always write the canonical snake_case form.
  delete merged.displayName

  const fieldMap = {
    display_name: profile.displayName,
    name: profile.name,
    picture: profile.picture,
    nip05: profile.nip05,
    about: profile.about,
  }
  for (const [key, value] of Object.entries(fieldMap)) {
    const trimmed = typeof value === 'string' ? value.trim() : value
    if (trimmed === '' || trimmed == null) {
      delete merged[key]
    } else {
      merged[key] = trimmed
    }
  }
  return JSON.stringify(merged)
}

// Unsigned kind:0 event template. The signer (finalizeEvent /
// window.nostr.signEvent) sets the pubkey field at sign time, so
// the pubkey param is accepted for forward compatibility but unused
// in the template body.
// eslint-disable-next-line no-unused-vars
export function buildProfileEvent(profile, existingProfile, pubkey) {
  return {
    kind: 0,
    created_at: Math.floor(Date.now() / 1000),
    content: buildProfileContent(profile, existingProfile),
    tags: [],
  }
}
